import { IAPIRoutes } from "../../../../config";

/**
 * Billing Module repository.
 *
 * Auth headers are injected automatically by `apiProvider.getDefaultHeaders()`
 * for `/patient-centric-app-v2/...` routes — we deliberately do NOT pass an
 * `Authorization` header here (see cip-rules.mdc).
 */
export const BillingModuleRepositoryFactory = ({
  APIProvider,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  APIRoutes: IAPIRoutes;
}): IBillingModuleRepository => {
  const { GET, PUT, POST } = APIProvider;

  // Pending Tasks V3 — unified monthly list (replaces the legacy
  // /billing/v11/clinic/monthly endpoint). The backend treats `clinic_id`
  // and `clinic_ids` independently (single wins over CSV); we forward only
  // the fields the caller set so the backend's parser can apply its
  // precedence rule.
  const getPendingTasksV3: IBillingModuleRepository["getPendingTasksV3"] = (
    payload
  ) => {
    const query: Record<string, string | number> = {
      selected_month: payload.selected_month,
    };
    if (typeof payload.clinic_id === "number") {
      query.clinic_id = payload.clinic_id;
    } else if (typeof payload.clinic_ids === "string" && payload.clinic_ids) {
      query.clinic_ids = payload.clinic_ids;
    }
    if (typeof payload.page === "number") query.page = payload.page;
    if (typeof payload.page_size === "number") {
      query.page_size = payload.page_size;
    }
    return GET(APIRoutes.apiGetPendingTasksV3, query);
  };

  const getBillingPatientMonthly: IBillingModuleRepository["getBillingPatientMonthly"] =
    (payload) =>
      GET(
        APIRoutes.apiGetBillingPatientMonthly.replace(
          "{patient_id}",
          String(payload.patient_id)
        ),
        {
          patient_id: payload.patient_id,
          clinic_id: payload.clinic_id,
          date: payload.date,
          regenerate: payload.regenerate ?? false,
        }
      );

  const getSignOffAssessment: IBillingModuleRepository["getSignOffAssessment"] =
    (payload) =>
      GET(
        APIRoutes.apiGetSignOffAssessment
          .replace("{clinic_id}", String(payload.clinic_id))
          .replace("{patient_id}", String(payload.patient_id))
          .replace("{billing_date}", payload.billing_date),
        {
          patient_id: payload.patient_id,
          clinic_id: payload.clinic_id,
          date: payload.billing_date,
        }
      );

  const updateBillingStatus: IBillingModuleRepository["updateBillingStatus"] = (
    payload
  ) =>
    PUT(
      APIRoutes.apiUpdateBillingStatus
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id))
        .replace("{billing_date}", payload.billing_date),
      payload.body
    );

  const updateSignOffAssessment: IBillingModuleRepository["updateSignOffAssessment"] =
    (payload) =>
      PUT(
        APIRoutes.apiUpdateSignOffAssessment
          .replace("{clinic_id}", String(payload.clinic_id))
          .replace("{patient_id}", String(payload.patient_id))
          .replace("{billing_date}", payload.billing_date),
        payload.body
      );

  // Per-patient per-month CPT charges. URL pattern mirrors legacy:
  //   /patient-centric-app-v2/billing/charges/clinic/{cid}/patient/{pid}?month=05&year=2026
  // The endpoint returns a flat `{ success, count, data: BillingCharge[] }`
  // wrapper — see `BillingChargesByPatientMonthResponse` in BillingModule.d.ts.
  const getBillingChargesByPatientMonth: IBillingModuleRepository["getBillingChargesByPatientMonth"] =
    (payload) =>
      GET(
        APIRoutes.apiGetBillingChargesByPatientMonth
          .replace("{clinic_id}", String(payload.clinic_id))
          .replace("{patient_id}", String(payload.patient_id)),
        {
          month: payload.month,
          year: payload.year,
        }
      );

  // Chart Review sign-off — same `billing-status/v5` endpoint as
  // `updateBillingStatus`. We send `payload.body` directly as the HTTP body;
  // it contains `file`, `json_data`, `datetime`, and `tracking_time` at the
  // top level to match the backend's v5 PUT contract.
  const signOffBillingMonth: IBillingModuleRepository["signOffBillingMonth"] = (
    payload
  ) =>
    PUT(
      APIRoutes.apiUpdateBillingStatus
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id))
        .replace("{billing_date}", payload.billing_date),
      payload.body
    );

  const markBillingAsBilled: IBillingModuleRepository["markBillingAsBilled"] = (
    payload
  ) =>
    PUT(
      APIRoutes.apiUpdateBillingStatus
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id))
        .replace("{billing_date}", payload.billing_date),
      payload.body
    );

  const createCarePlan: IBillingModuleRepository["createCarePlan"] = (
    payload
  ) =>
    POST(
      APIRoutes.apiCreateCarePlan
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id)),
      payload.body
    );

  const updateCarePlan: IBillingModuleRepository["updateCarePlan"] = (
    payload
  ) =>
    PUT(
      APIRoutes.apiUpdateCarePlan
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id))
        .replace("{care_plan_id}", String(payload.care_plan_id)),
      payload.body
    );

  return {
    getPendingTasksV3,
    getBillingPatientMonthly,
    getSignOffAssessment,
    updateBillingStatus,
    updateSignOffAssessment,
    getBillingChargesByPatientMonth,
    signOffBillingMonth,
    markBillingAsBilled,
    createCarePlan,
    updateCarePlan,
  };
};
